import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription } from './ui/alert';
import { 
  Upload, 
  FileText, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  Download,
  Save,
  Plus,
  Trash2,
  Sparkles,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Building2,
  Calendar,
  DollarSign,
  FileCheck,
  Loader2
} from 'lucide-react';
import API from '../services/apiBase';

const ContractBriefingsPage = () => {
  // State
  const [briefings, setBriefings] = useState([]);
  const [selectedBriefing, setSelectedBriefing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('upload');
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    contract: true,
    subcontract: false,
    agencies: false,
    regulations: false,
    clauses: false,
    provisions: false
  });

  // Form state (mirrors ContractBriefing model)
  const [formData, setFormData] = useState({
    contractor_name: '',
    contract_number: '',
    date_of_award: '',
    contract_type: '',
    estimated_cost: '',
    estimated_fee: '',
    total_price: '',
    period_of_performance_from: '',
    period_of_performance_to: '',
    is_subcontract: false,
    prime_contractor: {
      name: '',
      contract_no: '',
      contract_type: '',
      address: '',
      point_of_contact: '',
      phone: '',
      cognizant_dcaa_office: ''
    },
    acquisition_agency: {
      name: '',
      address: '',
      point_of_contact: '',
      phone: ''
    },
    admin_contract_office: {
      name: '',
      address: '',
      point_of_contact: '',
      phone: ''
    },
    procurement_regulations: {
      far_52_215_22: false,
      far_52_215_10: false,
      far_52_230_23: false,
      far_52_215_11: false,
      far_52_230_24: false,
      far_52_215_12: false,
      far_52_230_25: false,
      far_52_215_13: false,
      other_specify: ''
    },
    cas_clauses_identified: '',
    tina_clauses_identified: '',
    scope_of_work_statement: '',
    clauses_and_provisions: {
      far_52_252_2_incorporated: null,
      has_tm_or_fixed_price_schedule: null,
      tm_or_fixed_price_notes: '',
      is_cost_sharing_contract: null,
      cost_sharing_terms: '',
      has_level_of_effort_clause: null,
      level_of_effort_limitations: '',
      has_indirect_cost_ceilings: null,
      indirect_cost_ceiling_rates: '',
      is_fccm_allowable: null,
      has_far_penalty_clause: null,
      has_precontract_restrictions: null,
      precontract_restriction_details: '',
      has_overtime_restrictions: null,
      has_subcontract_restrictions: null,
      subcontract_restriction_details: '',
      unallowable_costs: '',
      profit_fee_provisions: '',
      other_special_provisions: ''
    }
  });

  // Load briefings on mount
  useEffect(() => {
    loadBriefings();
  }, []);

  const loadBriefings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/contract-briefings`);
      const data = await response.json();
      if (data.success) {
        setBriefings(data.briefings || []);
      }
    } catch (err) {
      console.error('Error loading briefings:', err);
      setError('Failed to load contract briefings');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a PDF or image file (PNG, JPG, WEBP)');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setSuccess(null);

      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user.id) {
        formDataUpload.append('user_id', user.id);
      }

      const response = await fetch(`${API}/contract-briefings/upload`, {
        method: 'POST',
        body: formDataUpload
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Contract analyzed successfully! Review and edit the extracted data below.');
        setSelectedBriefing(data.briefing);
        populateFormFromBriefing(data.briefing);
        setActiveTab('form');
        loadBriefings();
      } else {
        setError(data.detail || 'Failed to analyze contract');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError('Failed to upload and analyze contract');
    } finally {
      setUploading(false);
    }
  };

  const populateFormFromBriefing = (briefing) => {
    setFormData({
      contractor_name: briefing.contractor_name || '',
      contract_number: briefing.contract_number || '',
      date_of_award: briefing.date_of_award || '',
      contract_type: briefing.contract_type || '',
      estimated_cost: briefing.estimated_cost || '',
      estimated_fee: briefing.estimated_fee || '',
      total_price: briefing.total_price || '',
      period_of_performance_from: briefing.period_of_performance_from || '',
      period_of_performance_to: briefing.period_of_performance_to || '',
      is_subcontract: briefing.is_subcontract || false,
      prime_contractor: briefing.prime_contractor || {
        name: '', contract_no: '', contract_type: '', address: '', 
        point_of_contact: '', phone: '', cognizant_dcaa_office: ''
      },
      acquisition_agency: briefing.acquisition_agency || {
        name: '', address: '', point_of_contact: '', phone: ''
      },
      admin_contract_office: briefing.admin_contract_office || {
        name: '', address: '', point_of_contact: '', phone: ''
      },
      procurement_regulations: briefing.procurement_regulations || {
        far_52_215_22: false, far_52_215_10: false, far_52_230_23: false,
        far_52_215_11: false, far_52_230_24: false, far_52_215_12: false,
        far_52_230_25: false, far_52_215_13: false, other_specify: ''
      },
      cas_clauses_identified: briefing.cas_clauses_identified || '',
      tina_clauses_identified: briefing.tina_clauses_identified || '',
      scope_of_work_statement: briefing.scope_of_work_statement || '',
      clauses_and_provisions: briefing.clauses_and_provisions || {
        far_52_252_2_incorporated: null, has_tm_or_fixed_price_schedule: null,
        tm_or_fixed_price_notes: '', is_cost_sharing_contract: null,
        cost_sharing_terms: '', has_level_of_effort_clause: null,
        level_of_effort_limitations: '', has_indirect_cost_ceilings: null,
        indirect_cost_ceiling_rates: '', is_fccm_allowable: null,
        has_far_penalty_clause: null, has_precontract_restrictions: null,
        precontract_restriction_details: '', has_overtime_restrictions: null,
        has_subcontract_restrictions: null, subcontract_restriction_details: '',
        unallowable_costs: '', profit_fee_provisions: '', other_special_provisions: ''
      }
    });
  };

  const handleSave = async () => {
    if (!selectedBriefing?.id) {
      setError('No briefing selected to save');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const response = await fetch(`${API}/contract-briefings/${selectedBriefing.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Contract briefing saved successfully!');
        setSelectedBriefing(data.briefing);
        loadBriefings();
      } else {
        setError(data.detail || 'Failed to save briefing');
      }
    } catch (err) {
      console.error('Save error:', err);
      setError('Failed to save contract briefing');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    if (!selectedBriefing?.id) return;

    try {
      const response = await fetch(`${API}/contract-briefings/${selectedBriefing.id}/export`);
      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Contract_Briefing_${selectedBriefing.contract_number || 'unknown'}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
      setError('Failed to export contract briefing');
    }
  };

  const handleDelete = async (briefingId) => {
    if (!window.confirm('Are you sure you want to delete this contract briefing?')) return;

    try {
      const response = await fetch(`${API}/contract-briefings/${briefingId}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Contract briefing deleted');
        if (selectedBriefing?.id === briefingId) {
          setSelectedBriefing(null);
          setActiveTab('upload');
        }
        loadBriefings();
      } else {
        setError(data.detail || 'Failed to delete briefing');
      }
    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to delete contract briefing');
    }
  };

  const handleSelectBriefing = (briefing) => {
    setSelectedBriefing(briefing);
    populateFormFromBriefing(briefing);
    setActiveTab('form');
  };

  const createManualBriefing = async () => {
    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      const response = await fetch(`${API}/contract-briefings/manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id })
      });

      const data = await response.json();

      if (data.success) {
        setSelectedBriefing(data.briefing);
        populateFormFromBriefing(data.briefing);
        setActiveTab('form');
        loadBriefings();
        setSuccess('New briefing created. Fill in the details manually.');
      }
    } catch (err) {
      console.error('Error creating manual briefing:', err);
      setError('Failed to create new briefing');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const updateFormField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateNestedField = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  // Section Header Component
  const SectionHeader = ({ title, section, icon: Icon }) => (
    <div 
      className="flex items-center justify-between p-3 bg-gray-900 text-white rounded-t-lg cursor-pointer"
      onClick={() => toggleSection(section)}
    >
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-5 h-5" />}
        <span className="font-semibold">{title}</span>
      </div>
      {expandedSections[section] ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
    </div>
  );

  // Yes/No/NA Toggle Component
  const YesNoToggle = ({ value, onChange, label }) => (
    <div className="flex items-center gap-4">
      <Label className="min-w-[200px]">{label}</Label>
      <div className="flex gap-2">
        <Button
          type="button"
          variant={value === true ? "default" : "outline"}
          size="sm"
          onClick={() => onChange(true)}
          className={value === true ? "bg-green-600 hover:bg-green-700" : ""}
        >
          Yes
        </Button>
        <Button
          type="button"
          variant={value === false ? "default" : "outline"}
          size="sm"
          onClick={() => onChange(false)}
          className={value === false ? "bg-red-600 hover:bg-red-700" : ""}
        >
          No
        </Button>
        <Button
          type="button"
          variant={value === null ? "default" : "outline"}
          size="sm"
          onClick={() => onChange(null)}
          className={value === null ? "bg-gray-600 hover:bg-gray-700" : ""}
        >
          N/A
        </Button>
      </div>
    </div>
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Contract Briefings Summary</h1>
        <p className="text-gray-600 mt-1">
          Upload a contract document and let AI extract the briefing details automatically
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
          <button onClick={() => setError(null)} className="ml-auto">×</button>
        </Alert>
      )}
      {success && (
        <Alert className="mb-4 bg-green-50 border-green-200">
          <CheckCircle2 className="w-4 h-4 text-green-600" />
          <AlertDescription className="text-green-800">{success}</AlertDescription>
          <button onClick={() => setSuccess(null)} className="ml-auto">×</button>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left Panel - Briefings List */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Contract Briefings</CardTitle>
              <CardDescription>
                {briefings.length} briefing{briefings.length !== 1 ? 's' : ''} saved
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="px-4 pb-4 space-y-2">
                <Button 
                  onClick={() => setActiveTab('upload')} 
                  className="w-full" 
                  variant="outline"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload New Contract
                </Button>
                <Button 
                  onClick={createManualBriefing} 
                  className="w-full" 
                  variant="outline"
                  disabled={loading}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Manual Entry
                </Button>
              </div>
              <Separator />
              <div className="max-h-[400px] overflow-y-auto">
                {briefings.map((briefing) => (
                  <div
                    key={briefing.id}
                    className={`p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedBriefing?.id === briefing.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                    }`}
                    onClick={() => handleSelectBriefing(briefing)}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-sm truncate">
                          {briefing.contract_number || briefing.contractor_name || 'Untitled'}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {briefing.contractor_name || 'No contractor name'}
                        </p>
                        {briefing.ai_extracted && (
                          <Badge variant="secondary" className="mt-1 text-xs">
                            <Sparkles className="w-3 h-3 mr-1" />
                            AI Extracted
                          </Badge>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(briefing.id);
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
                {briefings.length === 0 && (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    No contract briefings yet. Upload a contract to get started.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Panel - Main Content */}
        <div className="lg:col-span-3">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="upload">
                <Upload className="w-4 h-4 mr-2" />
                Upload
              </TabsTrigger>
              <TabsTrigger value="form" disabled={!selectedBriefing}>
                <FileText className="w-4 h-4 mr-2" />
                Briefing Form
              </TabsTrigger>
            </TabsList>

            {/* Upload Tab */}
            <TabsContent value="upload">
              <Card>
                <CardHeader>
                  <CardTitle>Upload Contract Document</CardTitle>
                  <CardDescription>
                    Upload a PDF or image of your contract. Our AI will analyze it and extract all relevant information.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors">
                    {uploading ? (
                      <div className="flex flex-col items-center">
                        <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
                        <p className="text-lg font-medium">Analyzing contract...</p>
                        <p className="text-sm text-gray-500 mt-2">
                          AI is extracting contract details. This may take a moment.
                        </p>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-lg font-medium mb-2">
                          Drop your contract file here, or click to browse
                        </p>
                        <p className="text-sm text-gray-500 mb-4">
                          Supported formats: PDF, PNG, JPG, WEBP
                        </p>
                        <input
                          type="file"
                          accept=".pdf,.png,.jpg,.jpeg,.webp"
                          onChange={handleFileUpload}
                          className="hidden"
                          id="contract-upload"
                        />
                        <label htmlFor="contract-upload">
                          <Button asChild>
                            <span>Select File</span>
                          </Button>
                        </label>
                      </>
                    )}
                  </div>

                  <div className="mt-6 grid grid-cols-3 gap-4">
                    <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                      <Sparkles className="w-5 h-5 text-blue-500" />
                      <div className="text-sm">
                        <p className="font-medium">AI-Powered</p>
                        <p className="text-gray-500">GPT-5.1 analysis</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                      <FileCheck className="w-5 h-5 text-green-500" />
                      <div className="text-sm">
                        <p className="font-medium">Auto-Fill</p>
                        <p className="text-gray-500">27 form sections</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                      <Download className="w-5 h-5 text-blue-500" />
                      <div className="text-sm">
                        <p className="font-medium">Export</p>
                        <p className="text-gray-500">Excel format</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Form Tab */}
            <TabsContent value="form">
              {selectedBriefing && (
                <div className="space-y-4">
                  {/* AI Confidence Banner */}
                  {selectedBriefing.ai_extracted && selectedBriefing.ai_confidence && (
                    <Alert className="bg-blue-50 border-blue-200">
                      <Sparkles className="w-4 h-4 text-blue-600" />
                      <AlertDescription className="text-blue-800">
                        AI Confidence: {((selectedBriefing.ai_confidence.overall || 0) * 100).toFixed(0)}% — 
                        Review and edit the extracted data as needed.
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={handleExport}>
                      <Download className="w-4 h-4 mr-2" />
                      Export to Excel
                    </Button>
                    <Button onClick={handleSave} disabled={saving}>
                      {saving ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Save Changes
                    </Button>
                  </div>

                  {/* Section 1: Basic Information */}
                  <Card>
                    <SectionHeader title="1. BASIC CONTRACT INFORMATION" section="basic" icon={FileText} />
                    {expandedSections.basic && (
                      <CardContent className="pt-4 grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                          <Label>Contractor Name</Label>
                          <Input
                            value={formData.contractor_name}
                            onChange={(e) => updateFormField('contractor_name', e.target.value)}
                            placeholder="Enter contractor name"
                          />
                        </div>
                        <div>
                          <Label>Contract Number</Label>
                          <Input
                            value={formData.contract_number}
                            onChange={(e) => updateFormField('contract_number', e.target.value)}
                            placeholder="e.g., W911QX-21-C-0001"
                          />
                        </div>
                        <div>
                          <Label>Date of Award</Label>
                          <Input
                            type="date"
                            value={formData.date_of_award}
                            onChange={(e) => updateFormField('date_of_award', e.target.value)}
                          />
                        </div>
                      </CardContent>
                    )}
                  </Card>

                  {/* Section 2: Contract Details */}
                  <Card>
                    <SectionHeader title="2. CONTRACT DETAILS" section="contract" icon={DollarSign} />
                    {expandedSections.contract && (
                      <CardContent className="pt-4 grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                          <Label>Contract Type (Specify)</Label>
                          <Input
                            value={formData.contract_type}
                            onChange={(e) => updateFormField('contract_type', e.target.value)}
                            placeholder="e.g., CPFF, FFP, T&M, CPIF"
                          />
                        </div>
                        <div>
                          <Label>Estimated Cost ($)</Label>
                          <Input
                            type="number"
                            value={formData.estimated_cost}
                            onChange={(e) => updateFormField('estimated_cost', parseFloat(e.target.value) || '')}
                            placeholder="0.00"
                          />
                        </div>
                        <div>
                          <Label>Estimated Fee ($)</Label>
                          <Input
                            type="number"
                            value={formData.estimated_fee}
                            onChange={(e) => updateFormField('estimated_fee', parseFloat(e.target.value) || '')}
                            placeholder="0.00"
                          />
                        </div>
                        <div>
                          <Label>Total Price ($)</Label>
                          <Input
                            type="number"
                            value={formData.total_price}
                            onChange={(e) => updateFormField('total_price', parseFloat(e.target.value) || '')}
                            placeholder="0.00"
                          />
                        </div>
                        <div></div>
                        <div>
                          <Label>Period of Performance - From</Label>
                          <Input
                            type="date"
                            value={formData.period_of_performance_from}
                            onChange={(e) => updateFormField('period_of_performance_from', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>Period of Performance - To</Label>
                          <Input
                            type="date"
                            value={formData.period_of_performance_to}
                            onChange={(e) => updateFormField('period_of_performance_to', e.target.value)}
                          />
                        </div>
                      </CardContent>
                    )}
                  </Card>

                  {/* Section 3: Subcontract Information */}
                  <Card>
                    <SectionHeader title="3. SUBCONTRACT INFORMATION" section="subcontract" icon={Building2} />
                    {expandedSections.subcontract && (
                      <CardContent className="pt-4 space-y-4">
                        <div className="flex items-center gap-4">
                          <Label>Is this a Subcontract?</Label>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant={formData.is_subcontract === true ? "default" : "outline"}
                              size="sm"
                              onClick={() => updateFormField('is_subcontract', true)}
                            >
                              Yes
                            </Button>
                            <Button
                              type="button"
                              variant={formData.is_subcontract === false ? "default" : "outline"}
                              size="sm"
                              onClick={() => updateFormField('is_subcontract', false)}
                            >
                              No
                            </Button>
                          </div>
                        </div>
                        
                        {formData.is_subcontract && (
                          <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                            <div className="col-span-2">
                              <Label>Prime Contractor Name</Label>
                              <Input
                                value={formData.prime_contractor.name}
                                onChange={(e) => updateNestedField('prime_contractor', 'name', e.target.value)}
                              />
                            </div>
                            <div>
                              <Label>Prime Contract Number</Label>
                              <Input
                                value={formData.prime_contractor.contract_no}
                                onChange={(e) => updateNestedField('prime_contractor', 'contract_no', e.target.value)}
                              />
                            </div>
                            <div>
                              <Label>Contract Type</Label>
                              <Input
                                value={formData.prime_contractor.contract_type}
                                onChange={(e) => updateNestedField('prime_contractor', 'contract_type', e.target.value)}
                              />
                            </div>
                            <div className="col-span-2">
                              <Label>Address</Label>
                              <Input
                                value={formData.prime_contractor.address}
                                onChange={(e) => updateNestedField('prime_contractor', 'address', e.target.value)}
                              />
                            </div>
                            <div>
                              <Label>Point of Contact</Label>
                              <Input
                                value={formData.prime_contractor.point_of_contact}
                                onChange={(e) => updateNestedField('prime_contractor', 'point_of_contact', e.target.value)}
                              />
                            </div>
                            <div>
                              <Label>Phone</Label>
                              <Input
                                value={formData.prime_contractor.phone}
                                onChange={(e) => updateNestedField('prime_contractor', 'phone', e.target.value)}
                              />
                            </div>
                            <div className="col-span-2">
                              <Label>Cognizant DCAA Office</Label>
                              <Input
                                value={formData.prime_contractor.cognizant_dcaa_office}
                                onChange={(e) => updateNestedField('prime_contractor', 'cognizant_dcaa_office', e.target.value)}
                              />
                            </div>
                          </div>
                        )}
                      </CardContent>
                    )}
                  </Card>

                  {/* Section 4: Agencies */}
                  <Card>
                    <SectionHeader title="4. AGENCY INFORMATION" section="agencies" icon={Building2} />
                    {expandedSections.agencies && (
                      <CardContent className="pt-4 space-y-6">
                        <div>
                          <h4 className="font-semibold mb-3">Acquisition Agency</h4>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                              <Label>Name</Label>
                              <Input
                                value={formData.acquisition_agency.name}
                                onChange={(e) => updateNestedField('acquisition_agency', 'name', e.target.value)}
                              />
                            </div>
                            <div className="col-span-2">
                              <Label>Address</Label>
                              <Input
                                value={formData.acquisition_agency.address}
                                onChange={(e) => updateNestedField('acquisition_agency', 'address', e.target.value)}
                              />
                            </div>
                            <div>
                              <Label>Point of Contact</Label>
                              <Input
                                value={formData.acquisition_agency.point_of_contact}
                                onChange={(e) => updateNestedField('acquisition_agency', 'point_of_contact', e.target.value)}
                              />
                            </div>
                            <div>
                              <Label>Phone</Label>
                              <Input
                                value={formData.acquisition_agency.phone}
                                onChange={(e) => updateNestedField('acquisition_agency', 'phone', e.target.value)}
                              />
                            </div>
                          </div>
                        </div>

                        <Separator />

                        <div>
                          <h4 className="font-semibold mb-3">Administrative Contract Office</h4>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                              <Label>Name</Label>
                              <Input
                                value={formData.admin_contract_office.name}
                                onChange={(e) => updateNestedField('admin_contract_office', 'name', e.target.value)}
                              />
                            </div>
                            <div className="col-span-2">
                              <Label>Address</Label>
                              <Input
                                value={formData.admin_contract_office.address}
                                onChange={(e) => updateNestedField('admin_contract_office', 'address', e.target.value)}
                              />
                            </div>
                            <div>
                              <Label>Point of Contact</Label>
                              <Input
                                value={formData.admin_contract_office.point_of_contact}
                                onChange={(e) => updateNestedField('admin_contract_office', 'point_of_contact', e.target.value)}
                              />
                            </div>
                            <div>
                              <Label>Phone</Label>
                              <Input
                                value={formData.admin_contract_office.phone}
                                onChange={(e) => updateNestedField('admin_contract_office', 'phone', e.target.value)}
                              />
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    )}
                  </Card>

                  {/* Section 5: Procurement Regulations */}
                  <Card>
                    <SectionHeader title="5. PROCUREMENT REGULATIONS" section="regulations" icon={FileCheck} />
                    {expandedSections.regulations && (
                      <CardContent className="pt-4 space-y-4">
                        <p className="text-sm text-gray-500 mb-4">Check all that apply:</p>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={formData.procurement_regulations.far_52_215_22}
                              onCheckedChange={(checked) => updateNestedField('procurement_regulations', 'far_52_215_22', checked)}
                            />
                            <Label>FAR 52.215-22</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={formData.procurement_regulations.far_52_215_10}
                              onCheckedChange={(checked) => updateNestedField('procurement_regulations', 'far_52_215_10', checked)}
                            />
                            <Label>FAR 52.215-10</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={formData.procurement_regulations.far_52_230_23}
                              onCheckedChange={(checked) => updateNestedField('procurement_regulations', 'far_52_230_23', checked)}
                            />
                            <Label>FAR 52.230-23</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={formData.procurement_regulations.far_52_215_11}
                              onCheckedChange={(checked) => updateNestedField('procurement_regulations', 'far_52_215_11', checked)}
                            />
                            <Label>FAR 52.215-11</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={formData.procurement_regulations.far_52_230_24}
                              onCheckedChange={(checked) => updateNestedField('procurement_regulations', 'far_52_230_24', checked)}
                            />
                            <Label>FAR 52.230-24</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={formData.procurement_regulations.far_52_215_12}
                              onCheckedChange={(checked) => updateNestedField('procurement_regulations', 'far_52_215_12', checked)}
                            />
                            <Label>FAR 52.215-12</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={formData.procurement_regulations.far_52_230_25}
                              onCheckedChange={(checked) => updateNestedField('procurement_regulations', 'far_52_230_25', checked)}
                            />
                            <Label>FAR 52.230-25</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={formData.procurement_regulations.far_52_215_13}
                              onCheckedChange={(checked) => updateNestedField('procurement_regulations', 'far_52_215_13', checked)}
                            />
                            <Label>FAR 52.215-13</Label>
                          </div>
                        </div>
                        <div className="pt-4">
                          <Label>Other (Specify)</Label>
                          <Input
                            value={formData.procurement_regulations.other_specify}
                            onChange={(e) => updateNestedField('procurement_regulations', 'other_specify', e.target.value)}
                            placeholder="Enter other applicable regulations"
                          />
                        </div>
                      </CardContent>
                    )}
                  </Card>

                  {/* Section 6: CAS & TINA */}
                  <Card>
                    <SectionHeader title="6. CAS & TINA CLAUSES" section="clauses" icon={FileCheck} />
                    {expandedSections.clauses && (
                      <CardContent className="pt-4 space-y-4">
                        <div>
                          <Label>Cost Accounting Standards (CAS) Clauses</Label>
                          <Textarea
                            value={formData.cas_clauses_identified}
                            onChange={(e) => updateFormField('cas_clauses_identified', e.target.value)}
                            placeholder="Identify the CAS clauses contained in the contract"
                            rows={3}
                          />
                        </div>
                        <div>
                          <Label>Truth In Negotiation (TINA) Clauses</Label>
                          <Textarea
                            value={formData.tina_clauses_identified}
                            onChange={(e) => updateFormField('tina_clauses_identified', e.target.value)}
                            placeholder="Identify the TINA clauses contained in the contract"
                            rows={3}
                          />
                        </div>
                        <div>
                          <Label>Brief Statement of Scope of Work</Label>
                          <Textarea
                            value={formData.scope_of_work_statement}
                            onChange={(e) => updateFormField('scope_of_work_statement', e.target.value)}
                            placeholder="Describe the scope of work..."
                            rows={5}
                          />
                        </div>
                      </CardContent>
                    )}
                  </Card>

                  {/* Section 7: Contract Clauses and Special Provisions */}
                  <Card>
                    <SectionHeader title="7. CONTRACT CLAUSES AND SPECIAL PROVISIONS" section="provisions" icon={FileText} />
                    {expandedSections.provisions && (
                      <CardContent className="pt-4 space-y-6">
                        <YesNoToggle
                          label="15. FAR 52.252-2 Incorporated by Reference"
                          value={formData.clauses_and_provisions.far_52_252_2_incorporated}
                          onChange={(val) => updateNestedField('clauses_and_provisions', 'far_52_252_2_incorporated', val)}
                        />

                        <Separator />

                        <YesNoToggle
                          label="16. T&M or Fixed Price Schedule Attached"
                          value={formData.clauses_and_provisions.has_tm_or_fixed_price_schedule}
                          onChange={(val) => updateNestedField('clauses_and_provisions', 'has_tm_or_fixed_price_schedule', val)}
                        />
                        {formData.clauses_and_provisions.has_tm_or_fixed_price_schedule && (
                          <div className="ml-[216px]">
                            <Input
                              value={formData.clauses_and_provisions.tm_or_fixed_price_notes}
                              onChange={(e) => updateNestedField('clauses_and_provisions', 'tm_or_fixed_price_notes', e.target.value)}
                              placeholder="Notes about schedule..."
                            />
                          </div>
                        )}

                        <Separator />

                        <YesNoToggle
                          label="17. Cost Sharing Contract"
                          value={formData.clauses_and_provisions.is_cost_sharing_contract}
                          onChange={(val) => updateNestedField('clauses_and_provisions', 'is_cost_sharing_contract', val)}
                        />
                        {formData.clauses_and_provisions.is_cost_sharing_contract && (
                          <div className="ml-[216px]">
                            <Label>Cost Sharing Terms</Label>
                            <Textarea
                              value={formData.clauses_and_provisions.cost_sharing_terms}
                              onChange={(e) => updateNestedField('clauses_and_provisions', 'cost_sharing_terms', e.target.value)}
                              placeholder="Identify the terms of the cost sharing arrangement"
                              rows={2}
                            />
                          </div>
                        )}

                        <Separator />

                        <YesNoToggle
                          label="18. Level of Effort Clause"
                          value={formData.clauses_and_provisions.has_level_of_effort_clause}
                          onChange={(val) => updateNestedField('clauses_and_provisions', 'has_level_of_effort_clause', val)}
                        />
                        {formData.clauses_and_provisions.has_level_of_effort_clause && (
                          <div className="ml-[216px]">
                            <Label>Limitations</Label>
                            <Textarea
                              value={formData.clauses_and_provisions.level_of_effort_limitations}
                              onChange={(e) => updateNestedField('clauses_and_provisions', 'level_of_effort_limitations', e.target.value)}
                              placeholder="Identify the limitations specified in the contract"
                              rows={2}
                            />
                          </div>
                        )}

                        <Separator />

                        <YesNoToggle
                          label="19. Indirect Cost Ceilings"
                          value={formData.clauses_and_provisions.has_indirect_cost_ceilings}
                          onChange={(val) => updateNestedField('clauses_and_provisions', 'has_indirect_cost_ceilings', val)}
                        />
                        {formData.clauses_and_provisions.has_indirect_cost_ceilings && (
                          <div className="ml-[216px]">
                            <Label>Ceiling Rates</Label>
                            <Textarea
                              value={formData.clauses_and_provisions.indirect_cost_ceiling_rates}
                              onChange={(e) => updateNestedField('clauses_and_provisions', 'indirect_cost_ceiling_rates', e.target.value)}
                              placeholder="Identify the ceiling rates"
                              rows={2}
                            />
                          </div>
                        )}

                        <Separator />

                        <YesNoToggle
                          label="20. FCCM Allowable (FAR 52.215-30/16)"
                          value={formData.clauses_and_provisions.is_fccm_allowable}
                          onChange={(val) => updateNestedField('clauses_and_provisions', 'is_fccm_allowable', val)}
                        />

                        <Separator />

                        <YesNoToggle
                          label="21. FAR Penalty Clause (52.242-3)"
                          value={formData.clauses_and_provisions.has_far_penalty_clause}
                          onChange={(val) => updateNestedField('clauses_and_provisions', 'has_far_penalty_clause', val)}
                        />

                        <Separator />

                        <YesNoToggle
                          label="22. Precontract/Cost Allowability Restrictions"
                          value={formData.clauses_and_provisions.has_precontract_restrictions}
                          onChange={(val) => updateNestedField('clauses_and_provisions', 'has_precontract_restrictions', val)}
                        />
                        {formData.clauses_and_provisions.has_precontract_restrictions && (
                          <div className="ml-[216px]">
                            <Label>Restriction Details</Label>
                            <Textarea
                              value={formData.clauses_and_provisions.precontract_restriction_details}
                              onChange={(e) => updateNestedField('clauses_and_provisions', 'precontract_restriction_details', e.target.value)}
                              placeholder="Identify the relevant portions of the contract"
                              rows={2}
                            />
                          </div>
                        )}

                        <Separator />

                        <YesNoToggle
                          label="23. Overtime Restrictions (FAR 52.222-2)"
                          value={formData.clauses_and_provisions.has_overtime_restrictions}
                          onChange={(val) => updateNestedField('clauses_and_provisions', 'has_overtime_restrictions', val)}
                        />

                        <Separator />

                        <YesNoToggle
                          label="24. Subcontract Restrictions/Special Requirements"
                          value={formData.clauses_and_provisions.has_subcontract_restrictions}
                          onChange={(val) => updateNestedField('clauses_and_provisions', 'has_subcontract_restrictions', val)}
                        />
                        {formData.clauses_and_provisions.has_subcontract_restrictions && (
                          <div className="ml-[216px]">
                            <Label>Restriction Details</Label>
                            <Textarea
                              value={formData.clauses_and_provisions.subcontract_restriction_details}
                              onChange={(e) => updateNestedField('clauses_and_provisions', 'subcontract_restriction_details', e.target.value)}
                              placeholder="Identify the relevant portions of the contract"
                              rows={2}
                            />
                          </div>
                        )}

                        <Separator />

                        <div>
                          <Label>25. Unallowable Costs</Label>
                          <Textarea
                            value={formData.clauses_and_provisions.unallowable_costs}
                            onChange={(e) => updateNestedField('clauses_and_provisions', 'unallowable_costs', e.target.value)}
                            placeholder="Identify any costs made specifically unallowable by the terms of the contract"
                            rows={3}
                          />
                        </div>

                        <div>
                          <Label>26. Profit or Fee Provisions</Label>
                          <Textarea
                            value={formData.clauses_and_provisions.profit_fee_provisions}
                            onChange={(e) => updateNestedField('clauses_and_provisions', 'profit_fee_provisions', e.target.value)}
                            placeholder="Identify any profit or fee provisions in the contract"
                            rows={3}
                          />
                        </div>

                        <div>
                          <Label>27. Other Special Provisions/Limitations</Label>
                          <Textarea
                            value={formData.clauses_and_provisions.other_special_provisions}
                            onChange={(e) => updateNestedField('clauses_and_provisions', 'other_special_provisions', e.target.value)}
                            placeholder="Identify other special provisions/limitations specified in the contract"
                            rows={3}
                          />
                        </div>
                      </CardContent>
                    )}
                  </Card>

                  {/* Bottom Action Bar */}
                  <div className="flex justify-end gap-4 pt-4 border-t">
                    <Button variant="outline" onClick={handleExport}>
                      <Download className="w-4 h-4 mr-2" />
                      Export to Excel
                    </Button>
                    <Button onClick={handleSave} disabled={saving}>
                      {saving ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Save Changes
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default ContractBriefingsPage;
